/**
 * Speech to text for the autocue, behind one small interface. The engine is the browser's own recogniser (the Web
 * Speech API): on the machine itself where the browser has the language installed, otherwise the browser's speech
 * service — Google's, in Chrome; Microsoft's, in Edge — which then hears the microphone. Which of the two may be used
 * is the installation's choice (AUTOCUE_SPEECH): off (the default), on-device only, or either.
 *
 * The microphone is opened here (the one chosen, or the system's), so the page can show its level, and handed to the
 * recogniser; a browser too old to take it hears the system's microphone instead. A recogniser stops on its own
 * after a silence or a minute or so: it is started again at once, for as long as the autocue listens.
 *
 * The level comes from an audio context made in the very click that starts the listening (Safari, on an iPad or an
 * iPhone, keeps one made later asleep, its meter at nothing), woken again whenever Safari puts it to sleep (the page
 * in the background, a call). In Safari the microphone is asked for as its recogniser asks for it (no changes to its
 * echo and noise handling), so the two share it. When the meter hears nothing while the recogniser hears words (an
 * iPad has been seen to do this), the audio is made again once or twice; failing that, the meter shows the words being
 * heard instead of the microphone's level, and says so (`MeterMix`), with what the browser reports (`diag`).
 */

export type SpeechMode = 'off' | 'on-device' | 'browser';
/**
 * What stops the listening for good. 'in-app': iOS gives no speech recognition to a web app opened from the Home
 * Screen (WebKit bug 225298), only to Safari itself; the autocue says so instead of listening to nothing (ADR 095).
 */
export type SpeechProblem = 'unsupported' | 'in-app' | 'denied' | 'no-on-device' | 'downloading' | 'failed';
/**
 * What the page shows while listening: hearing; the speech service unreachable and being tried again; or the service
 * hearing speech but returning no words (Edge's, seen on 2/11/2026: empty results every few seconds while Chrome's
 * returned the same audio as words), which leaves the autocue nothing to follow.
 */
export type SpeechStatus = 'listening' | 'network' | 'empty';
/** Whose speech service the browser's recogniser uses, for the page to say so. */
export type SpeechService = 'google' | 'microsoft' | 'apple' | 'other';

interface RecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [i: number]: { transcript: string };
}
interface RecognitionEventLike {
  readonly resultIndex: number;
  readonly results: { readonly length: number; readonly [i: number]: RecognitionResultLike };
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  processLocally?: boolean;
  onresult: ((e: RecognitionEventLike) => void) | null;
  onspeechstart?: (() => void) | null;
  onspeechend?: (() => void) | null;
  onerror: ((e: { error: string; message?: string }) => void) | null;
  onend: (() => void) | null;
  start(track?: MediaStreamTrack): void;
  abort(): void;
}
interface RecognitionOptions {
  langs: string[];
  processLocally?: boolean;
}
interface RecognitionClass {
  new (): Recognition;
  available?: (o: RecognitionOptions) => Promise<string>;
  install?: (o: RecognitionOptions) => Promise<boolean>;
}

function engine(): RecognitionClass | null {
  const w = window as unknown as { SpeechRecognition?: RecognitionClass; webkitSpeechRecognition?: RecognitionClass };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export class SpeechError extends Error {
  constructor(
    readonly problem: SpeechProblem,
    detail = '',
  ) {
    super(detail || problem);
  }
}

export interface ListenOptions {
  mode: Exclude<SpeechMode, 'off'>;
  /** BCP 47, "el-GR". */
  lang: string;
  /** The microphone chosen; '' = the system's. */
  deviceId: string;
  /** What was heard since the last final phrase, revised as more is heard; final = it will not change. */
  onText: (text: string, final: boolean) => void;
  onStatus: (s: SpeechStatus) => void;
  /** Listening stopped for good (the listener is already stopped). */
  onProblem: (p: SpeechProblem, detail: string) => void;
}

/** What the meter shows: the microphone's loudness, or (where the browser gives none) how much the recogniser is hearing. */
export interface Level {
  value: number;
  source: 'mic' | 'words';
}

export interface Listening {
  /** Heard on this machine, not by a speech service. */
  readonly local: boolean;
  /** The meter now, 0 to 1; null when the browser gives nothing to show yet. */
  level(): Level | null;
  /** What the browser reports of the meter's audio and the microphone, for when the meter does not move. */
  diag(): string;
  stop(): void;
}

/** The recogniser has been hearing words this long since the meter last heard anything: the meter is not hearing the microphone. */
const DEAF_MS = 4000;
/** Below this the microphone is taken as silent. */
const QUIET = 0.02;
/** How fast the words' meter falls back between words. */
const WORDS_DECAY_MS = 350;

/**
 * The meter's reading from the microphone's level and the recogniser's words: the level while it is believable (it
 * has been heard, and the recogniser does not hear words it misses), otherwise the words being heard, rising with
 * each result and falling back between them; nothing before either has been heard.
 */
export class MeterMix {
  private peak = 0;
  private loudAt = -Infinity;
  private wordsAt = -Infinity;
  private speaking = false;
  private act = 0;
  private lastT = 0;

  heardWords(now: number): void {
    this.decay(now);
    this.wordsAt = now;
    this.act = Math.min(1, this.act + 0.45);
  }

  /** The words' meter falls back with the time since it last moved. */
  private decay(now: number): void {
    this.act *= Math.exp(-Math.max(0, now - this.lastT) / WORDS_DECAY_MS);
    this.lastT = now;
  }

  speech(active: boolean): void {
    this.speaking = active;
  }

  /** The microphone has never been heard although words have. */
  get deaf(): boolean {
    return this.wordsAt > -Infinity && (this.peak <= QUIET || this.wordsAt - this.loudAt > DEAF_MS);
  }

  read(mic: number | null, now: number): Level | null {
    this.decay(now);
    if (mic !== null && mic > QUIET) {
      this.loudAt = now;
      this.peak = Math.max(this.peak, mic);
    }
    if (mic !== null && !this.deaf) return { value: mic, source: 'mic' };
    if (this.wordsAt > -Infinity || this.speaking) return { value: Math.max(this.act, this.speaking ? 0.12 : 0), source: 'words' };
    return null;
  }
}

/** Safari (WebKit): its recogniser and the page share the microphone, asked for the same way. */
const isWebKit = (): boolean => typeof navigator !== 'undefined' && /Apple/.test(navigator.vendor) && !('chrome' in window);
/** Running as an installed app (opened from the Home Screen or a desktop icon). */
const isInstalledApp = (): boolean => typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as { standalone?: boolean }).standalone === true);
/** iOS keeps speech recognition from a web app opened from the Home Screen; Safari itself has it. */
export const noVoiceInApp = (webkit: boolean, installedApp: boolean): boolean => webkit && installedApp;
/** A recogniser that ends this soon after starting, this many times in a row without a result, is not hearing anything. */
const QUICK_END_MS = 1500;
const QUICK_ENDS = 5;

/**
 * The speech service behind the browser's recogniser: Apple's in every browser on an iPad or an iPhone and in Safari
 * (WebKit), Microsoft's in Edge, Google's in Chrome; another Chromium (Opera, Samsung, Vivaldi, Yandex) is not known.
 */
export function speechService(ua: string, webkit: boolean): SpeechService {
  if (webkit) return 'apple';
  if (/\bEdg\//.test(ua)) return 'microsoft';
  if (/\b(OPR|SamsungBrowser|Vivaldi|YaBrowser)\//.test(ua)) return 'other';
  return /\bChrome\//.test(ua) ? 'google' : 'other';
}

/** This browser's speech service. */
export const browserSpeechService = (): SpeechService => (typeof navigator === 'undefined' ? 'other' : speechService(navigator.userAgent, isWebKit()));

/** Results without words in a row, and how long without words, past which the service is taken as returning none. */
const EMPTY_RESULTS = 3;
const WORDLESS_MS = 10_000;

/**
 * Words or none, result after result: 'empty' once the service has given only results without words, three in a
 * row and for ten seconds; 'listening' again with the first words.
 */
export class WordWatch {
  private empties = 0;

  constructor(private wordsAt: number) {}

  result(words: boolean, now: number): 'listening' | 'empty' {
    if (words) {
      this.empties = 0;
      this.wordsAt = now;
      return 'listening';
    }
    this.empties++;
    return this.empties >= EMPTY_RESULTS && now - this.wordsAt >= WORDLESS_MS ? 'empty' : 'listening';
  }
}

/** Whether the language can be recognised on this machine: true, false, or 'downloading' when the browser has started fetching it. */
async function onDevice(Engine: RecognitionClass, lang: string): Promise<boolean | 'downloading'> {
  if (!Engine.available) return false;
  try {
    const st = await Engine.available({ langs: [lang], processLocally: true });
    if (st === 'available') return true;
    if ((st === 'downloadable' || st === 'downloading') && Engine.install) {
      void Engine.install({ langs: [lang], processLocally: true }).catch(() => undefined);
      return 'downloading';
    }
  } catch {
    // a policy forbids it, or the browser half-implements it: not on this machine
  }
  return false;
}

/** An audio context, or null where there is none (Safari before 14.1 has only the prefixed one). */
function newAudio(): AudioContext | null {
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  try {
    return Ctx ? new Ctx() : null;
  } catch {
    return null;
  }
}

export async function listen(o: ListenOptions): Promise<Listening> {
  const Engine = engine();
  if (!Engine) throw new SpeechError('unsupported');
  if (noVoiceInApp(isWebKit(), isInstalledApp())) throw new SpeechError('in-app', 'iOS home-screen app');
  // the meter's audio, made and woken now, in the click that starts the listening, before anything is awaited
  let audio = newAudio();
  const wake = () => {
    if (audio && audio.state !== 'running' && audio.state !== 'closed') void audio.resume().catch(() => undefined);
  };
  wake();
  const quit = (e: SpeechError) => {
    void audio?.close().catch(() => undefined);
    return e;
  };
  const here = await onDevice(Engine, o.lang);
  if (o.mode === 'on-device' && here !== true) throw quit(new SpeechError(here === 'downloading' ? 'downloading' : 'no-on-device'));
  const local = here === true;

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: isWebKit() ? (o.deviceId ? { deviceId: { exact: o.deviceId } } : true) : { ...(o.deviceId ? { deviceId: { exact: o.deviceId } } : {}), echoCancellation: false, noiseSuppression: false, autoGainControl: true },
    });
  } catch (e) {
    const denied = e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError');
    throw quit(new SpeechError(denied ? 'denied' : 'failed', e instanceof Error ? e.message : String(e)));
  }
  const track = stream.getAudioTracks()[0];
  if (!track) throw quit(new SpeechError('failed', 'no audio track'));

  // the microphone into an analyser, pulled by the output at no volume (older WebKit runs only what reaches it)
  let meter: AnalyserNode | null = null;
  const connect = () => {
    meter = null;
    if (!audio) return;
    try {
      const a = audio.createAnalyser();
      a.fftSize = 1024;
      audio.createMediaStreamSource(stream).connect(a);
      const silent = audio.createGain();
      silent.gain.value = 0;
      a.connect(silent);
      silent.connect(audio.destination);
      // Safari puts it to sleep with the page in the background or a call: woken again, at the next touch at the latest
      audio.onstatechange = wake;
      meter = a;
    } catch {
      meter = null;
    }
  };
  connect();
  window.addEventListener('pointerdown', wake);
  window.addEventListener('keydown', wake);
  document.addEventListener('visibilitychange', wake);
  const floats = new Float32Array(1024);
  const bytes = new Uint8Array(1024);
  const mix = new MeterMix();
  // a meter that hears nothing while the recogniser hears words: its audio made again (twice at most), now that the
  // microphone is the page's (Safari lets a page that captures start its audio)
  let rebuilt = 0;
  let rebuiltAt = performance.now();
  const rebuild = () => {
    rebuilt++;
    rebuiltAt = performance.now();
    void audio?.close().catch(() => undefined);
    audio = newAudio();
    wake();
    connect();
  };

  let stopped = false;
  let current: Recognition | null = null;
  let timer = 0;
  let failures = 0;
  // a recogniser ending at once, again and again, without a result: the browser's last error says why, if it said
  let quickEnds = 0;
  let lastError = '';
  const watch = new WordWatch(performance.now());
  let shown: SpeechStatus = 'listening';
  const say = (s: SpeechStatus) => {
    if (s === shown) return;
    shown = s;
    o.onStatus(s);
  };
  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.clearTimeout(timer);
    current?.abort();
    for (const t of stream.getTracks()) t.stop();
    window.removeEventListener('pointerdown', wake);
    window.removeEventListener('keydown', wake);
    document.removeEventListener('visibilitychange', wake);
    void audio?.close().catch(() => undefined);
  };
  const fail = (p: SpeechProblem, detail: string) => {
    stop();
    o.onProblem(p, detail);
  };

  const begin = () => {
    if (stopped) return;
    const r = new Engine();
    r.lang = o.lang;
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;
    if (local) r.processLocally = true;
    // a recogniser's results run from its start: the final ones are passed on once, the rest are what is being heard
    let passed = 0;
    const startedAt = performance.now();
    let heardAny = false;
    r.onspeechstart = () => mix.speech(true);
    r.onspeechend = () => mix.speech(false);
    r.onresult = (e) => {
      failures = 0;
      heardAny = true;
      const now = performance.now();
      let heard = '';
      let words = false;
      for (let i = passed; i < e.results.length; i++) {
        const res = e.results[i]!;
        const text = res[0]?.transcript ?? '';
        if (text.trim()) words = true;
        if (res.isFinal && i === passed) {
          passed++;
          if (text.trim()) o.onText(text, true);
        } else heard += ` ${text}`;
      }
      if (words) mix.heardWords(now);
      say(watch.result(words, now));
      if (heard.trim()) o.onText(heard.trim(), false);
    };
    r.onerror = (e) => {
      if (stopped) return;
      lastError = e.message ? `${e.error}: ${e.message}` : e.error;
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') fail('denied', lastError);
      else if (e.error === 'language-not-supported') fail(local ? 'no-on-device' : 'failed', lastError);
      else if (e.error === 'network') {
        failures++;
        say('network');
      }
      // 'no-speech', 'aborted', 'audio-capture': the recogniser ends and is started again
    };
    r.onend = () => {
      if (stopped) return;
      quickEnds = !heardAny && performance.now() - startedAt < QUICK_END_MS ? quickEnds + 1 : 0;
      if (quickEnds >= QUICK_ENDS) {
        fail('failed', `ends at once ×${quickEnds}${lastError ? `, last error ${lastError}` : ', no error reported'}`);
        return;
      }
      timer = window.setTimeout(begin, Math.min(5000, 150 * 2 ** failures));
    };
    current = r;
    try {
      r.start(track);
    } catch (e) {
      fail('failed', e instanceof Error ? e.message : String(e));
    }
  };
  begin();
  o.onStatus('listening');

  return {
    local,
    level: () => {
      const now = performance.now();
      let mic: number | null = null;
      const m = meter as AnalyserNode | null;
      if (m && audio?.state === 'running' && !track.muted && track.readyState !== 'ended') {
        let sum = 0;
        if (typeof m.getFloatTimeDomainData === 'function') {
          m.getFloatTimeDomainData(floats);
          for (const v of floats) sum += v * v;
        } else {
          m.getByteTimeDomainData(bytes);
          for (const b of bytes) sum += ((b - 128) / 128) ** 2;
        }
        mic = Math.min(1, Math.sqrt(sum / m.fftSize) * 4);
      }
      const shown = mix.read(mic, now);
      if (mix.deaf && rebuilt < 2 && now - rebuiltAt > 3000) rebuild();
      return shown;
    },
    diag: () => `audio ${audio?.state ?? 'none'} · mic ${track.muted ? 'muted' : track.readyState}${track.enabled ? '' : ' off'}${rebuilt ? ` · again ${rebuilt}` : ''}${isWebKit() ? ' · WebKit' : ''}`,
    stop,
  };
}

/** The microphones of this machine; their names show once the page may use one. */
export async function microphones(): Promise<Array<{ id: string; label: string }>> {
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter((d) => d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications').map((d) => ({ id: d.deviceId, label: d.label }));
  } catch {
    return [];
  }
}
